import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import IcomoonReact from 'icomoon-react';
import Text from '../../../shared/components/Text/Text';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import { updateCalendarSettings } from '../ducks/calendarActions';

const ModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: #444444;
`;

const TabsContainer = styled('div')`
  display: flex;
  gap: 32px;
  border-bottom: 1px solid #EEEEEE;
  margin-bottom: 24px;
`;

const Tab = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${(props) => (props.isActive ? '#444444' : '#9E9E9E')};
  padding-bottom: 8px;
  border-bottom: 2px solid ${(props) => (props.isActive ? '#444444' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #444444;
  }
`;

const ContentSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
`;

const SelectContainer = styled('div')`
  .react-select__control {
    border: 1px solid #EEEEEE;
    border-radius: 4px;
    min-height: 40px;
    box-shadow: none;

    &:hover {
      border-color: #444444;
    }
  }

  .react-select__value-container {
    padding: 2px 12px;
  }

  .react-select__single-value {
    color: #444444;
    font-family: 'Nunito Sans';
    font-size: 14px;
  }

  .react-select__menu {
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  .react-select__option {
    font-family: 'Nunito Sans';
    font-size: 14px;
    color: #444444;
    cursor: pointer;
    
    &:active {
      background-color: #F5F5F5;
    }
  }

  .react-select__option--is-selected {
    background-color: #F5F5F5;
    color: #444444;
  }

  .react-select__option--is-focused {
    background-color: #F5F5F5;
  }
`;

const ImageUploadArea = styled('div')`
  width: 100%;
  height: 120px;
  border: 1px dashed ${(props) => (props.isDragActive ? '#444444' : '#EEEEEE')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.isDragActive ? 'rgba(68, 68, 68, 0.05)' : 'white')};
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #444444;
    background: rgba(68, 68, 68, 0.05);
  }
`;

const ImagePreviewContainer = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F5F5F5;
`;

const ImagePreview = styled('img')`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const RemoveButton = styled('div')`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ImageUploadText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  margin-top: 8px;
`;

const FileTypeText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9E9E9E;
`;

const ErrorText = styled(Text)`
  color: #E53935;
  font-size: 12px;
  margin-top: 8px;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const Button = styled('button')`
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ primary }) => (primary ? '#444444' : 'white')};
  color: ${({ primary }) => (primary ? 'white' : '#444444')};
  border: ${({ primary }) => (primary ? 'none' : '1px solid #444444')};

  &:hover {
    ${({ primary }) => (primary ? 'opacity: 0.9;' : 'background: #F5F5F5;')}
  }
`;

const ShortcutSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ShortcutCategory = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CategoryTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 700;
  color: #444444;
`;

const ShortcutRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #F5F5F5;
`;

const ShortcutAction = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
`;

const ShortcutKey = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #666666;
  background: #F5F5F5;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #EEEEEE;
`;

const RowContainer = styled('div')`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const HalfWidthFormGroup = styled(FormGroup)`
  flex: 1;
`;

const SettingsModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('General');
  const tabs = ['General', 'Keyboard Shortcuts'];
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedView, setSelectedView] = useState({ value: 'none', label: 'None' });
  const [selectedDay, setSelectedDay] = useState({ value: 'monday', label: 'Monday' });
  const [headerImage, setHeaderImage] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  const viewOptions = [
    { value: 'none', label: 'None' },
    { value: 'day', label: 'Day View' },
    { value: 'week', label: 'Week View' },
  ];

  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const hasAutofilledDefaultCalendar = useRef(false);
  const userTouchedPickerRef = useRef(false);

  const handleCalendarChange = (option) => {
    userTouchedPickerRef.current = true;
    setSelectedCalendar(option);
  };

  useEffect(() => {
    if (userTouchedPickerRef.current) return;
    if (hasAutofilledDefaultCalendar.current) return;
    if (!currentUser?.defaultCalendar || !parentCalendars?.length) return;

    const { defaultCalendar, defaultCalendarParentId } = currentUser;
    let match = null;

    parentCalendars.forEach((parent) => {
      if (defaultCalendarParentId) {
        if (parent.id !== defaultCalendarParentId) return;
        parent.childCalendars?.forEach((child) => {
          if (child.id === defaultCalendar) {
            match = {
              value: child.id,
              label: child.name,
              isChild: true,
              parentId: parent.id,
            };
          }
        });
      } else if (parent.id === defaultCalendar) {
        match = {
          value: parent.id,
          label: parent.name,
          isParent: true,
        };
      }
    });

    if (match) {
      setSelectedCalendar(match);
      hasAutofilledDefaultCalendar.current = true;
    }
  }, [currentUser?.defaultCalendar, currentUser?.defaultCalendarParentId, parentCalendars]);

  const getCalendarOptions = () => {
    const options = [
      { value: 0, label: 'Select a default calendar' },
    ];

    parentCalendars?.forEach((parent) => {
      // Add parent calendar
      options.push({
        value: parent.id,
        label: parent.name,
        isParent: true,
      });

      // Add child calendars if they exist
      if (parent.childCalendars?.length > 0) {
        parent.childCalendars.forEach((child) => {
          options.push({
            value: child.id,
            label: child.name,
            isChild: true,
            parentId: parent.id,
          });
        });
      }
    });

    return options;
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.isChild ? '32px' : '16px',
      backgroundColor: state.isSelected ? '#F5F5F5' : 'white',
      color: '#444444',
      '&:hover': {
        backgroundColor: '#F5F5F5',
      },
    }),
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setUploadError('Please upload a valid .jpg or .jpeg file');
      return;
    }

    const file = acceptedFiles[0];
    setUploadError(null);
    setHeaderImage({
      file,
      preview: URL.createObjectURL(file),
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setHeaderImage(null);
    setUploadError(null);
  };

  const handleSave = () => {
    if (selectedCalendar) {
      dispatch(updateCalendarSettings(
        currentUser.accountCode,
        currentUser.id,
        { defaultCalendar: selectedCalendar.value },
        {
          onSuccess: () => {
            closeModal();
          },
          onError: (error) => {
            console.error('Failed to update calendar settings:', error);
          },
        },
      ));
    } else {
      closeModal();
    }
  };

  return (
    <ModalContent>
      <Header>
        <Title>Settings</Title>
      </Header>

      <TabsContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab}
            isActive={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </Tab>
        ))}
      </TabsContainer>

      {activeTab === 'General' && (
        <ContentSection>
          <FormGroup>
            <Label>Default Calendar</Label>
            <SelectContainer>
              <Select
                options={getCalendarOptions()}
                value={selectedCalendar}
                onChange={handleCalendarChange}
                styles={customStyles}
                className='react-select'
                classNamePrefix='react-select'
                placeholder='Select a calendar'
                isSearchable
                autoFocus
              />
            </SelectContainer>
          </FormGroup>

          {/* <RowContainer>
            <HalfWidthFormGroup>
              <Label>Default View</Label>
              <SelectContainer>
                <Select
                  options={viewOptions}
                  value={selectedView}
                  onChange={setSelectedView}
                  styles={customStyles}
                  className='react-select'
                  classNamePrefix='react-select'
                  isSearchable={false}
                />
              </SelectContainer>
            </HalfWidthFormGroup>

            <HalfWidthFormGroup>
              <Label>Start Week On</Label>
              <SelectContainer>
                <Select
                  options={dayOptions}
                  value={selectedDay}
                  onChange={setSelectedDay}
                  styles={customStyles}
                  className='react-select'
                  classNamePrefix='react-select'
                  isSearchable={false}
                />
              </SelectContainer>
            </HalfWidthFormGroup>
          </RowContainer>

          <FormGroup>
            <Label>Header Image</Label>
            <Text style={{ fontSize: '12px', color: '#9E9E9E', marginBottom: '8px' }}>
              Recommended size: 1060 x 420
            </Text>
            <ImageUploadArea {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              {headerImage ? (
                <ImagePreviewContainer>
                  <ImagePreview src={headerImage.preview} alt='Header preview' />
                  <RemoveButton onClick={handleRemoveImage}>
                    <IcomoonReact iconSet={iconSet} icon='close' size={16} color='#666666' />
                  </RemoveButton>
                </ImagePreviewContainer>
              ) : (
                <>
                  <ImageUploadText>Click or drag image here.</ImageUploadText>
                  <FileTypeText>Accepted file types: .jpg and .jpeg</FileTypeText>
                </>
              )}
            </ImageUploadArea>
            {uploadError && <ErrorText>{uploadError}</ErrorText>}
          </FormGroup> */}

          <ButtonContainer>
            <Button primary onClick={handleSave}>Save</Button>
            <Button onClick={closeModal}>Close</Button>
          </ButtonContainer>
        </ContentSection>
      )}

      {activeTab === 'Keyboard Shortcuts' && (
        <ShortcutSection>
          <ShortcutCategory>
            <CategoryTitle>Calendar</CategoryTitle>
            <ShortcutRow>
              <ShortcutAction>Copy Selected Items</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+C</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Paste Items</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+V</ShortcutKey>
            </ShortcutRow>
          </ShortcutCategory>
          <ShortcutCategory>
            <CategoryTitle>Workout</CategoryTitle>
            <ShortcutRow>
              <ShortcutAction>Add Exercise</ShortcutAction>
              <ShortcutKey>Ctrl+N</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Create Exercise while Adding</ShortcutAction>
              <ShortcutKey>Shift+Enter</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Add Property</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+P</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Delete Exercise</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+D</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Add/Edit Additional Info</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+I</ShortcutKey>
            </ShortcutRow>
          </ShortcutCategory>
        </ShortcutSection>
      )}
    </ModalContent>
  );
};

SettingsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default SettingsModal;
